'use client';

import { useEffect } from 'react';
import { useProfileFetch } from './useProfileFetch';
import { useSupabaseAuth } from '../auth/simplified/SupabaseAuthContext';
import { createBrowserClient } from '@/lib/supabase-clients';

/**
 * Component that applies fixes for staging.apprendo.co domain
 * This component doesn't render anything visible, but sets up auth fixes
 * Updated to work with Supabase's native auth
 */
export function StagingDomainAuthFix() {
  const { fetchProfile } = useProfileFetch();
  const auth = useSupabaseAuth();
  
  useEffect(() => {
    // Only run on staging domain
    if (typeof window === 'undefined') return;
    if (!window.location.hostname.includes('staging.apprendo.co')) return;
    
    console.log('[StagingDomainAuthFix] Setting up auth fixes for staging domain');
    
    // Check if we have a valid session
    const supabase = createBrowserClient();
    
    // Check for access_token in URL hash on login callback
    const checkForSession = async () => {
      if (window.location.hash && window.location.hash.includes('access_token=')) {
        console.log('[StagingDomainAuthFix] Found access_token in URL hash');
        
        try {
          // Let Supabase complete the OAuth flow
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('[StagingDomainAuthFix] Error getting session:', error.message);
            return false;
          }
          
          if (data.session) {
            console.log('[StagingDomainAuthFix] Successfully established session');
            
            // Store userId for cross-domain requests
            const userId = data.session.user.id;
            localStorage.setItem('staging_auth_user_id', userId);
            
            // Remove the hash to prevent repeated processing
            if (window.history.replaceState) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
            
            // Force a page reload to apply the new auth state
            if (window.location.pathname.includes('/login')) {
              console.log('[StagingDomainAuthFix] Redirecting to dashboard from login page');
              window.location.href = '/dashboard';
              return true;
            }
          }
        } catch (e) {
          console.error('[StagingDomainAuthFix] Error handling URL hash:', e);
        }
      }
      
      return false;
    };
    
    // Check for URL tokens first
    checkForSession().then(foundSessionInUrl => {
      if (foundSessionInUrl) return;
      
      // Check if we already have a valid session
      supabase.auth.getSession().then(({ data, error }) => {
        if (error) {
          console.error('[StagingDomainAuthFix] Error getting session:', error.message);
          return;
        }
        
        if (data.session) {
          console.log('[StagingDomainAuthFix] Found existing session:', data.session.user.id);
          
          // Store userId for cross-domain requests
          localStorage.setItem('staging_auth_user_id', data.session.user.id);
          
          // Redirect to dashboard if on login page
          if (window.location.pathname.includes('/login')) {
            console.log('[StagingDomainAuthFix] On login page with valid session, redirecting to dashboard');
            window.location.href = '/dashboard';
          }
          
          // Use fetchProfile to ensure profile data is available
          fetchProfile().then(result => {
            console.log('[StagingDomainAuthFix] Profile fetch result:', result ? 'success' : 'failed');
          });
        } else if (window.location.pathname.includes('/dashboard')) {
          // If on dashboard without session, redirect to login
          console.log('[StagingDomainAuthFix] On dashboard without valid session, redirecting to login');
          window.location.href = '/login';
        }
      });
    });
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[StagingDomainAuthFix] Auth state changed:', event);
      
      // Ignore TOKEN_REFRESHED events to avoid interfering with auth context
      if (event === 'TOKEN_REFRESHED') {
        console.log('[StagingDomainAuthFix] Ignoring token refresh to avoid auth conflicts');
        return;
      }
      
      if (event === 'SIGNED_IN' && session) {
        console.log('[StagingDomainAuthFix] User signed in:', session.user.id);
        
        // Store userId for cross-domain requests
        localStorage.setItem('staging_auth_user_id', session.user.id);
        
        // Redirect to dashboard if on login page
        if (window.location.pathname.includes('/login')) {
          console.log('[StagingDomainAuthFix] Redirecting to dashboard after sign in');
          window.location.href = '/dashboard';
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('[StagingDomainAuthFix] User signed out');
        
        // Clear userId from localStorage
        localStorage.removeItem('staging_auth_user_id');
        
        // Redirect to login if on dashboard
        if (window.location.pathname.includes('/dashboard')) {
          window.location.href = '/login';
        }
      }
    });
    
    // Set up a global fetch interceptor for API calls to handle cross-domain issues
    const originalFetch = window.fetch;
    window.fetch = async function(input, init) {
      // Get the URL from the input
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      // Skip prefetch requests to dashboard routes that don't exist yet
      // These cause unnecessary auth refreshes during navigation
      if (url.includes('_rsc=') && url.includes('/dashboard/') && 
          (url.includes('/chat') || url.includes('/stats') || 
           url.includes('/podcast') || url.includes('/recommendations') || 
           url.includes('/subscribers') || url.includes('/settings'))) {
        // Let the prefetch 404 without causing auth issues
        return originalFetch(input, init);
      }
      
      // Only intercept API calls
      if (url.includes('/api/')) {
        try {
          // Use getUser() to securely validate the session
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          
          // Get the session for the access token
          const { data: { session } } = await supabase.auth.getSession();
          
          if (user && session?.access_token) {
            // Create new headers to avoid modifying original
            const newInit = { ...init, headers: { ...(init?.headers || {}) } };
            
            // Add verified user ID and access token to headers
            newInit.headers = {
              ...newInit.headers,
              'x-user-id': user.id,
              'Authorization': `Bearer ${session.access_token}`,
              'x-staging-fix': 'true'
            };
            
            // Use the modified init
            return originalFetch(input, newInit);
          }
        } catch (error) {
          console.error('[StagingDomainAuthFix] Error intercepting fetch:', error);
        }
      }
      
      // For all other requests, use original fetch
      return originalFetch(input, init);
    };
    
    // Cleanup function
    return () => {
      // Unsubscribe from auth changes
      subscription.unsubscribe();
      
      // Restore original fetch
      window.fetch = originalFetch;
    };
  }, [fetchProfile, auth]);
  
  // This component doesn't render anything
  return null;
}